import {
  ChevronsUpDown,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Users,
  Wallet,
  Zap,
} from "lucide-react";
import { useEffect, useState } from "react";

import { sourceMark } from "../lib/source-marks.js";
import { Skeleton } from "./ui/skeleton.js";

import { cn } from "../lib/cn.js";
import {
  cellText,
  columnBands,
  columnTotal,
  hasGroups,
  hasUnattributed,
  type ColumnBand,
  type ReportColumn,
  type ReportRow,
  type Unattributed,
} from "../lib/reports.js";

/**
 * One table for all five reports, banded like legacy's: Platform / First click / Last
 * click above the metrics they own, with a rule between each band.
 *
 * EVERY STATE IS DRAWN, and they are drawn DIFFERENTLY. Legacy rendered an empty table
 * body for "loading", for "no rows in this range" and for "the request failed" alike —
 * three very different situations that all looked like "this store has no data", which is
 * the one reading that makes a merchant stop trusting the product.
 *
 * A SORTABLE HEADER IS A BUTTON, an unsortable one is plain text. The API's `sortBy` is a
 * closed enum per report; a header that looks clickable but 400s is worse than one that
 * never invited the click.
 */
/**
 * A mark per header band, so the attribution models are told apart at a glance rather
 * than by reading three similar words. Legacy puts a gold bolt on FC and LC for the same
 * reason. A band with no entry simply gets no icon — the table must not depend on this
 * map being exhaustive.
 */
const BAND_MARKS: Readonly<
  Record<string, { readonly Icon: typeof Zap; readonly className: string }>
> = {
  Platform: { Icon: Wallet, className: "text-sky-500" },
  "First click": { Icon: Zap, className: "text-amber-500" },
  "Last click": { Icon: Zap, className: "text-amber-500" },
  "Data-driven": { Icon: Sparkles, className: "text-violet-500" },
  Traffic: { Icon: Users, className: "text-emerald-500" },
};

/** A column with no explicit `width` — wide enough for a money/ratio cell's widest reading. */
const DEFAULT_COLUMN_WIDTH = 140;

/** A resize never leaves a column narrower than this — matches legacy's `CustomTable.jsx`. */
const MIN_COLUMN_WIDTH = 70;

/** Text that fills its cell and truncates only when the column is genuinely too narrow. */
function TextCell({ text }: { readonly text: string }): React.JSX.Element {
  return (
    <span title={text} className="block w-full min-w-0 truncate">
      {text}
    </span>
  );
}

/**
 * Left offsets for the FROZEN PREFIX of columns — legacy freezes the name/status column
 * plus the whole "Platform" group so a merchant scrolling into FC/LC/DDA/Linear/Triple
 * territory never loses sight of what they're spending. `position: sticky` stacks several
 * columns only when each one's `left` equals the summed width of every sticky column
 * before it — an auto-width cell breaks that arithmetic the moment its content is wider
 * than assumed, so every sticky column gets a FIXED width and a matching `width`/
 * `minWidth` style at every render site below.
 *
 * Column 0 is always sticky (it always was); columns after it join the frozen prefix only
 * while `column.sticky` stays true — the prefix STOPS at the first non-sticky column, so a
 * report with no `sticky` columns beyond the first keeps exactly today's one-column freeze.
 *
 * AND ONLY WHEN THE MERCHANT LOCKS THEM (2026-10-21). Frozen by default, Name + Status + the six
 * Platform columns took ~970 px: on a 1366 px laptop 64 px of the table scrolled (uat, 13 Oct),
 * so FC/LC/DDA/EA columns slid under a wall that never moved. Now only column 0 is frozen unless
 * `lockAll` — the toolbar's "Lock platform columns" — asks for the whole prefix.
 *
 * WIDTHS COME FROM `colWidths`, THE LIVE RESIZE STATE, not the report definition's static
 * `column.width` — dragging a sticky column's edge must shift every sticky column after it,
 * exactly as it shifts every ordinary column's rendered position.
 */
function stickyOffsets(
  columns: readonly ReportColumn[],
  colWidths: readonly number[],
  lockAll: boolean,
): readonly number[] {
  const offsets: number[] = [];
  let cursor = 0;
  for (let index = 0; index < columns.length; index += 1) {
    const column = columns[index];
    if (column === undefined) break;
    if (index > 0 && (!lockAll || column.sticky !== true)) break;
    offsets[index] = cursor;
    cursor += colWidths[index] ?? column.width ?? DEFAULT_COLUMN_WIDTH;
  }
  return offsets;
}

/** The inline style every cell needs: `width`/`minWidth` from the live resize state, plus `left` for the sticky ones. */
function columnStyle(
  width: number,
  offset: number | undefined,
): React.CSSProperties {
  const style: React.CSSProperties = {
    width: `${String(width)}px`,
    minWidth: `${String(width)}px`,
  };
  if (offset !== undefined) style.left = `${String(offset)}px`;
  return style;
}

/**
 * The draggable strip on a header cell's right edge — legacy's `CustomTable.jsx` puts the
 * exact same 6px hit target there. It is a SIBLING of the header's label/sort button, not a
 * child of it, so dragging it can never also trigger a sort.
 *
 * `spansBandRow` stretches it up over the band row too. A leaf `<th>` — "Spend", "Revenue",
 * "ROAS" — is only as tall as ITS OWN row, so the plain `inset-y-0` handle used to cover
 * just that lower strip: a merchant reaching for the border at the BAND row's height (where
 * "Platform" reads above three or four columns) found nothing to grab. The band row's own
 * `<th>` is `h-11` (44px) — see the `h-11` on that row below — so extending the handle by
 * exactly that much upward makes the whole column border draggable from top to bottom, not
 * only its lower half.
 */
function ResizeHandle({
  index,
  onResize,
  spansBandRow = false,
}: {
  readonly index: number;
  readonly onResize: (index: number, event: React.MouseEvent) => void;
  readonly spansBandRow?: boolean;
}): React.JSX.Element {
  return (
    <span
      onMouseDown={(event) => onResize(index, event)}
      onClick={(event) => event.stopPropagation()}
      className={cn(
        "absolute right-0 w-2.5 cursor-col-resize touch-none select-none z-40 group/resize hover:bg-primary/20 transition-colors",
        // z-40: above BOTH header rows' own z-20/z-30 stacking, regardless of which one
        // is later in DOM order — the handle must win that comparison unconditionally,
        // not by relying on a tie it happens to win today.
        spansBandRow ? "-top-11 bottom-0" : "inset-y-0",
      )}
    >
      <span className="absolute right-0 top-0 bottom-0 w-[2px] bg-transparent group-hover/resize:bg-primary transition-colors" />
    </span>
  );
}

export function ReportTable({
  columns,
  rows,
  loading,
  sortBy,
  direction,
  onSort,
  onRowClick,
  totalLabel,
  totals,
  unattributed,
  unattributedLabel,
  onUnattributedClick,
  onJourneyOpen,
  journeyRowEligible,
  storeName,
  emptyReason,
  search,
  lockPlatformColumns = false,
}: {
  readonly columns: readonly ReportColumn[];
  readonly rows: readonly ReportRow[];
  readonly loading: boolean;
  readonly sortBy: string | null;
  readonly direction: "asc" | "desc";
  readonly onSort: (sortKey: string) => void;
  readonly onRowClick?: (row: ReportRow) => void;
  /** "Total" when these rows are the whole result, "Page total" when they are not. */
  readonly totalLabel: string;
  /**
   * Window-wide sums from the API. When present the footer is the TOTAL, whatever page is
   * on screen; without them it falls back to summing the visible rows.
   */
  readonly totals?: Readonly<Record<string, number>> | null;
  /** Campaign reports only: the orders that resolved to no campaign at all. */
  readonly unattributed?: Unattributed | null;
  /** What the reconciliation row is called on THIS report. */
  readonly unattributedLabel?: string;
  /**
   * Opens the breakdown of WHY these orders are unmatched. Absent for reports and levels
   * that have no such endpoint — `supportsUnmatchedReasons` decides — in which case the
   * label stays plain text rather than becoming a button that 404s.
   */
  readonly onUnattributedClick?: () => void;
  /**
   * Opens the Customer Journey & Orders modal for a Purchases cell (a column with a
   * `journeyModel`). `row` is `null` for the "not matched" line under the table.
   */
  readonly onJourneyOpen?: (
    row: ReportRow | null,
    column: ReportColumn,
  ) => void;
  /** Which rows have Purchases cells that can open one — not `[Blank]`, not the DDA `pending` bucket. Absent = all. */
  readonly journeyRowEligible?: (row: ReportRow) => boolean;
  /** Named in the empty state, so "no data" says WHICH store had none. */
  readonly storeName?: string;
  /**
   * `no-orders` when the window held no orders at all — a different problem from a narrow range.
   * `no-matches` when a search found nothing: the range is fine, the needle is not in it.
   */
  readonly emptyReason?: "no-orders" | "no-rows" | "no-matches";
  /** The search that found nothing, quoted back in the `no-matches` state. */
  readonly search?: string;
  /** Freeze every `sticky` column (Status, Platform), not only the first — see stickyOffsets. */
  readonly lockPlatformColumns?: boolean;
}): React.JSX.Element {
  const banded = hasGroups(columns);
  const bands = columnBands(columns);

  /**
   * EVERY COLUMN'S WIDTH IS LIVE STATE, not the report definition's fixed number — legacy's
   * `CustomTable.jsx` lets a merchant drag any header's right edge, and this is the same
   * mechanism: seed from the report's own widths, then let the drag win until the column
   * SET itself changes (switching reports, or an attribution objective changing which
   * columns show) — a sort, a date-range change, or fresh rows for the same columns must
   * never discard a resize the merchant just made.
   */
  const [colWidths, setColWidths] = useState<number[]>(() =>
    columns.map((column) => column.width ?? DEFAULT_COLUMN_WIDTH),
  );
  useEffect(() => {
    setColWidths(columns.map((column) => column.width ?? DEFAULT_COLUMN_WIDTH));
  }, [columns]);

  const widthAt = (index: number): number =>
    colWidths[index] ?? columns[index]?.width ?? DEFAULT_COLUMN_WIDTH;

  const startResize = (index: number, event: React.MouseEvent): void => {
    event.preventDefault();
    const startX = event.clientX;
    const startWidth = widthAt(index);
    const onMouseMove = (moveEvent: MouseEvent): void => {
      const nextWidth = startWidth + (moveEvent.clientX - startX);
      setColWidths((previous) =>
        previous.map((width, i) =>
          i === index ? Math.max(nextWidth, MIN_COLUMN_WIDTH) : width,
        ),
      );
    };
    const onMouseUp = (): void => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  const offsets = stickyOffsets(columns, colWidths, lockPlatformColumns);
  // The border that marks where the frozen prefix ENDS belongs on its LAST column only —
  // every column before it is just as frozen but must stay borderless, exactly as an
  // ordinary internal column always has been. `offsets` is a contiguous run from index 0,
  // so its own length is that last index.
  const lastStickyIndex = offsets.length - 1;

  // Which columns START a band, so a vertical rule can be drawn there and nowhere else.
  const bandStarts = new Set<number>();
  let cursor = 0;
  for (const band of bands) {
    bandStarts.add(cursor);
    cursor += band.span;
  }

  const showFooter = columns.some((column) => column.total !== undefined);

  /**
   * The rows the FOOTER adds up — the API's window-wide sums when it sent them, the
   * visible page otherwise, plus the unmatched line.
   *
   * The unmatched line used to be shown beside the total but left out of it, so a
   * campaign table could disagree with Source Attribution on the same window: 3,443
   * orders there against 2,666 here (2026-09-03). Including it is what makes
   * `campaign rows + unmatched = the platform's orders` true in the arithmetic and not
   * only in the note.
   */
  const totalRows: readonly ReportRow[] = [
    ...(totals == null ? rows : [totals]),
    ...(hasUnattributed(unattributed)
      ? [unattributed as unknown as ReportRow]
      : []),
  ];

  /**
   * A SKELETON, NOT THE WORD "Loading…".
   *
   * The word told the merchant nothing and, worse, occupied a few lines where a full table
   * was about to appear — so the page jumped the moment data landed, and anything below it
   * moved under the cursor. The skeleton renders the REAL header and the real column
   * count, so the layout that arrives is the layout already on screen; only the grey bars
   * turn into numbers.
   *
   * Eight rows: enough to read as a table, few enough that a report returning three rows
   * does not collapse noticeably.
   */
  if (loading && rows.length === 0) {
    return (
      <Shell
        columns={columns}
        bands={bands}
        bandStarts={bandStarts}
        offsets={offsets}
        lastStickyIndex={lastStickyIndex}
        colWidths={colWidths}
        onResize={startResize}
        banded={banded}
        sortBy={null}
        direction="desc"
        onSort={() => undefined}
      >
        <tbody aria-busy="true" aria-label="Loading report">
          {Array.from({ length: 8 }, (_, rowIndex) => (
            <tr
              key={rowIndex}
              className={rowIndex % 2 === 1 ? "row-odd" : "row-even"}
            >
              {columns.map((column, index) => (
                <td
                  key={column.key}
                  className={cn(
                    "overflow-hidden border-b px-3 py-3",
                    bandStarts.has(index) && index > 0 && "border-l",
                    offsets[index] !== undefined &&
                      `sticky z-10 ${rowIndex % 2 === 1 ? "row-odd" : "row-even"}`,
                    index === lastStickyIndex && "border-r",
                  )}
                  style={columnStyle(widthAt(index), offsets[index])}
                >
                  {/* Narrower for numbers, wider for the label column — a row of identical
                      bars reads as a loading spinner, not as the table it precedes. */}
                  <Skeleton
                    className={cn("h-4", index === 0 ? "w-40" : "ml-auto w-16")}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </Shell>
    );
  }

  if (!loading && rows.length === 0 && emptyReason === "no-matches") {
    return (
      <div className="text-muted-foreground px-4 py-14 text-center text-sm">
        <p>
          Nothing matches “{search}”
          {storeName === undefined ? "" : ` for ${storeName}`} in this range.
        </p>
        <p className="mt-1">
          The search covers every row of the report, not just this page. Clear
          it to see all rows.
        </p>
      </div>
    );
  }

  if (!loading && rows.length === 0) {
    /**
     * "TRY A WIDER RANGE" WAS OFTEN THE WRONG ADVICE. Dev has two stores and only one of
     * them has ever sent an event, so selecting the other showed this message and blamed
     * the dates — sending someone to widen a range that was never the problem. When the
     * window holds no orders at all, the store is the more likely explanation, and Store
     * Health is the page that answers it.
     */
    return (
      <div className="text-muted-foreground px-4 py-14 text-center text-sm">
        <p>
          No data{storeName === undefined ? "" : ` for ${storeName}`} in this
          range.
        </p>
        <p className="mt-1">
          {emptyReason === "no-orders"
            ? "This store recorded no orders in these dates. If it was connected recently, tracking may not have started yet — Store Health shows when data began arriving."
            : "Try a wider range — attribution needs orders and ad spend inside the window."}
        </p>
      </div>
    );
  }

  return (
    /**
     * THE TABLE SCROLLS, NOT THE PAGE — in both directions.
     *
     * Vertically it used to be the page that moved, which carried the column headers off
     * the top: fifty rows in and every number was under an unlabelled column, on a table
     * that is eighteen columns wide. Now this box scrolls and the header and total rows
     * stay pinned to its edges, so a figure always has a label above it and a total below.
     *
     * `max-height` rather than a fixed height: a report with four rows should be four rows
     * tall, not a mostly-empty pane.
     */
    <Shell
      columns={columns}
      bands={bands}
      bandStarts={bandStarts}
      offsets={offsets}
      lastStickyIndex={lastStickyIndex}
      colWidths={colWidths}
      onResize={startResize}
      banded={banded}
      sortBy={sortBy}
      direction={direction}
      onSort={onSort}
      loading={loading}
    >
      <tbody>
        {rows.map((row, rowIndex) => (
          // The index is part of the key deliberately: these reports have no unique id
          // per row, and the DOW report repeats a URL once per week, so `url` alone
          // would collide and React would reuse the wrong row on re-sort.
          <tr
            key={`${String(row[columns[0]?.key ?? ""] ?? "")}:${rowIndex}`}
            className={cn(
              "transition-colors",
              rowIndex % 2 === 1 ? "row-odd" : "row-even",
            )}
          >
            {columns.map((column, index) => {
              const numeric =
                column.kind !== "text" &&
                column.kind !== "week" &&
                column.kind !== "status";
              const text = cellText(column.kind, row[column.key], column.key);
              return (
                <td
                  key={column.key}
                  className={cn(
                    "overflow-hidden border-b pl-3 pr-4 py-3 whitespace-nowrap",
                    numeric ? "text-right tabular-nums" : "text-left",
                    bandStarts.has(index) && index > 0 && "border-l",
                    offsets[index] !== undefined &&
                      `sticky z-10 ${rowIndex % 2 === 1 ? "row-odd" : "row-even"}`,
                    index === lastStickyIndex && "border-r",
                  )}
                  style={columnStyle(widthAt(index), offsets[index])}
                >
                  {onRowClick !== undefined && index === 0 ? (
                    <button
                      type="button"
                      className="text-primary hover:underline"
                      onClick={() => onRowClick(row)}
                    >
                      {column.kind === "status" ? (
                        <StatusCell value={row[column.key]} />
                      ) : column.kind === "text" ? (
                        column.brandMark === true ? (
                          <SourceCell value={text} />
                        ) : (
                          <TextCell text={text} />
                        )
                      ) : (
                        text
                      )}
                    </button>
                  ) : column.kind === "status" ? (
                    <StatusCell value={row[column.key]} />
                  ) : column.kind === "text" ? (
                    column.brandMark === true ? (
                      <SourceCell value={text} />
                    ) : (
                      <TextCell text={text} />
                    )
                  ) : column.journeyModel !== undefined &&
                    onJourneyOpen !== undefined &&
                    (journeyRowEligible?.(row) ?? true) &&
                    Number(row[column.key] ?? 0) > 0 ? (
                    <JourneyLink
                      text={text}
                      title={`View the ${text} ${column.group ?? ""} orders behind this number`.replace(
                        "  ",
                        " ",
                      )}
                      onClick={() => onJourneyOpen(row, column)}
                    />
                  ) : (
                    text
                  )}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>

      {/*
          WHAT THE FOOTER ADDS UP.

          The API's window-wide sums when it sent them, the visible rows otherwise — PLUS
          the unmatched line, so `campaign rows + unmatched = the platform's orders` holds
          in the footer and not just in the note beside it. `columnTotal` is unchanged, so
          ratios stay derived by the one rule that is mutation-tested: a total ROAS is
          total revenue over total spend, never an average of the rows.
        */}
      {showFooter && (
        <tfoot>
          {/*
              THE ROW THAT MAKES THE TWO REPORTS RECONCILE.

              A campaign report can only show orders whose click resolved to a campaign.
              Without this line, the rest are simply absent, and the table silently
              disagrees with Source Attribution — which is exactly what happened on
              2026-09-03 (3,443 there, 2,666 here). It sits ABOVE the total and is
              included in it, so `campaigns + unattributed = the platform's orders`.
            */}
          {/*
              INCLUDED in the Total below, as of 2026-09-07 (owner request).

              It used to be excluded, on the reasoning that these orders have revenue and
              no spend so folding them in would inflate ROAS. That is right per CAMPAIGN
              and wrong for the TOTAL: the footer's spend is the platform's whole spend and
              its revenue is the platform's whole attributed revenue, so including this
              line is what makes the total ROAS equal Source Attribution's `lc_ea_roas` for
              the same platform. Leaving it out made the two pages disagree on revenue AND
              on ROAS — 3,443 orders against 2,666 on 2026-09-03.
            */}
          {hasUnattributed(unattributed) && (
            <tr className="row-odd text-muted-foreground">
              {columns.map((column, index) => {
                const numeric =
                  column.kind !== "text" && column.kind !== "week";
                const value =
                  unattributed === null || unattributed === undefined
                    ? null
                    : unattributedValue(column.key, unattributed);
                return (
                  <td
                    key={column.key}
                    className={cn(
                      "overflow-hidden border-t px-3 py-2.5 whitespace-nowrap",
                      numeric ? "text-right tabular-nums" : "text-left",
                      bandStarts.has(index) && index > 0 && "border-l",
                      offsets[index] !== undefined &&
                        "row-odd sticky z-10 italic",
                      index === lastStickyIndex && "border-r",
                    )}
                    style={columnStyle(widthAt(index), offsets[index])}
                  >
                    {index === 0 ? (
                      onUnattributedClick !== undefined ? (
                        <button
                          type="button"
                          className="text-primary not-italic hover:underline"
                          onClick={onUnattributedClick}
                        >
                          {unattributedLabel ?? "Not matched"}
                          <span aria-hidden="true"> ›</span>
                        </button>
                      ) : (
                        (unattributedLabel ?? "Not matched")
                      )
                    ) : value === null ? (
                      "—"
                    ) : // Only first / last click have an unmatched count of their own to open
                    // (the remainder query measures just those two), so only those two
                    // become links; every other model reads "—" above.
                    onJourneyOpen !== undefined &&
                      (column.journeyModel === "fc" ||
                        column.journeyModel === "lc") &&
                      Number(value) > 0 ? (
                      <JourneyLink
                        text={cellText(column.kind, value, column.key)}
                        title={`View the ${cellText(column.kind, value, column.key)} orders not matched to a campaign`}
                        onClick={() => onJourneyOpen(null, column)}
                      />
                    ) : (
                      cellText(column.kind, value, column.key)
                    )}
                  </td>
                );
              })}
            </tr>
          )}
          <tr className="row-head text-foreground font-semibold">
            {columns.map((column, index) => {
              const numeric = column.kind !== "text" && column.kind !== "week";
              /**
               * The API's window-wide sums when it sent them, the visible rows
               * otherwise. `columnTotal` is unchanged either way, so ratios are still
               * derived by the one rule that is mutation-tested — a total ROAS stays
               * total revenue over total spend rather than an average.
               *
               * `kind: "distinct"` is the one exception: it MUST NOT see the unattributed
               * line appended above. That line is real to `campaign rows + unmatched =
               * the platform's orders`, which is exactly the kind of addition a distinct
               * visitor count cannot survive — `totalRows` growing to 2 entries (the API's
               * totals row plus the unmatched row) made `columnTotal`'s "only trust a
               * single row" safeguard refuse a real total and show "—" for every report
               * with a reconciliation line (Meta/Google Campaign Attribution). Source
               * Attribution has no such line, which is why this bug never showed there.
               */
              const total =
                column.total?.kind === "distinct" ||
                (column.total?.kind === "ratio" && column.total.totalsOnly === true)
                  ? columnTotal(column, totals == null ? rows : [totals])
                  : columnTotal(column, totalRows);
              return (
                <td
                  key={column.key}
                  className={cn(
                    "row-head sticky bottom-0 z-20 overflow-hidden border-t-2 pl-3 pr-4 py-3 whitespace-nowrap",
                    numeric ? "text-right tabular-nums" : "text-left",
                    bandStarts.has(index) && index > 0 && "border-l",
                    // Frozen too, so "Page total" cannot scroll out of sight — which is
                    // exactly how it was mistaken for the range's total (2026-09-03).
                    offsets[index] !== undefined && "row-head z-30",
                    index === lastStickyIndex && "border-r",
                  )}
                  style={columnStyle(widthAt(index), offsets[index])}
                >
                  {index === 0 ? (
                    totalLabel
                  ) : total === null ? (
                    // The dash EXPLAINS ITSELF. A bare one reads as "broken"; the real
                    // reason is a sentence, and a tooltip costs no width on a table
                    // that is already eighteen columns across.
                    <span
                      title={
                        column.noTotalReason ?? "No total for this column."
                      }
                      className="cursor-help"
                    >
                      —
                    </span>
                  ) : (
                    cellText(column.kind, total, column.key)
                  )}
                </td>
              );
            })}
          </tr>
        </tfoot>
      )}
    </Shell>
  );
}

/** A Purchases number that opens the orders behind it — legacy's blue, semibold, underline-on-hover link. */
function JourneyLink({
  text,
  title,
  onClick,
}: {
  readonly text: string;
  readonly title: string;
  readonly onClick: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className="cursor-pointer font-semibold text-blue-600 not-italic transition-colors hover:text-blue-800 hover:underline"
    >
      {text}
    </button>
  );
}

function BandLabel({
  label,
}: {
  readonly label: string | null;
}): React.JSX.Element | null {
  if (label === null) return null;
  const mark = BAND_MARKS[label];
  if (mark === undefined) return <>{label}</>;
  const { Icon, className } = mark;
  return (
    <span className="inline-flex items-center justify-center gap-1.5">
      <Icon className={`size-3.5 ${className}`} aria-hidden="true" />
      {label}
    </span>
  );
}

/**
 * Active in blue, inactive in grey — owner's request, 2026-09-07.
 *
 * A DOT PLUS A WORD, not colour alone. Roughly one man in twelve cannot separate these
 * two hues reliably, and a table whose only signal is a colour is unreadable to them; the
 * word carries the meaning and the colour makes it scannable.
 *
 * The reconciliation row ("Not matched to a campaign") has no status and never will —
 * those orders matched no campaign, so there is nothing to be on or off. It renders an em
 * dash rather than borrowing "inactive", which would be a claim we cannot support.
 */
function StatusCell({
  value,
}: {
  readonly value: string | number | null | undefined;
}): React.JSX.Element {
  if (value !== "active" && value !== "inactive")
    return <span className="text-muted-foreground">—</span>;
  const active = value === "active";
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium",
        active
          ? "bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300"
          : "bg-muted text-muted-foreground",
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          "size-1.5 rounded-full",
          active ? "bg-blue-600 dark:bg-blue-400" : "bg-muted-foreground/50",
        )}
      />
      {active ? "Active" : "Inactive"}
    </span>
  );
}

function SourceCell({ value }: { readonly value: string }): React.JSX.Element {
  const { Icon, color } = sourceMark(value);
  return (
    <span title={value} className="flex min-w-0 items-center gap-2">
      {Icon === null ? (
        // A neutral dot rather than a blank: it keeps the column's text aligned whether
        // or not we recognise the source, and an unknown source is the NORMAL case —
        // `classifySource` keeps a merchant's own utm_source verbatim.
        <span
          aria-hidden="true"
          className="bg-muted-foreground/40 size-2 shrink-0 rounded-full"
        />
      ) : (
        <Icon
          aria-hidden="true"
          className="size-4 shrink-0"
          style={{ color }}
        />
      )}
      <span className="min-w-0 truncate">{value}</span>
    </span>
  );
}

/**
 * Which of the unattributed figures belongs in a given column.
 *
 * Only the four the remainder query actually measures. Spend, impressions and clicks are
 * PLATFORM figures reported per campaign — there is no such thing as spend that belongs
 * to no campaign, so those cells stay empty rather than showing a zero that would read as
 * "we spent nothing" instead of "this does not apply".
 */
function unattributedValue(
  key: string,
  row: Unattributed,
): string | number | null {
  return row[key] ?? null;
}

/**
 * The scroll box and the header, in ONE place.
 *
 * The skeleton and the real table render the same header from the same columns, so the
 * layout a merchant sees while loading is the layout that arrives — no jump, and no second
 * copy of the banding logic to drift out of step with the first.
 */
function Shell({
  columns,
  bands,
  bandStarts,
  offsets,
  lastStickyIndex: _lastStickyIndex,
  colWidths,
  onResize,
  banded,
  sortBy,
  direction,
  onSort,
  loading = false,
  children,
}: {
  readonly columns: readonly ReportColumn[];
  readonly bands: readonly ColumnBand[];
  readonly bandStarts: ReadonlySet<number>;
  readonly offsets: readonly number[];
  readonly lastStickyIndex: number;
  /** Live per-column widths — the drag state a merchant's resize writes to. */
  readonly colWidths: readonly number[];
  readonly onResize: (index: number, event: React.MouseEvent) => void;
  readonly banded: boolean;
  readonly sortBy: string | null;
  readonly direction: "asc" | "desc";
  readonly onSort: (sortKey: string) => void;
  readonly loading?: boolean;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  const widthAt = (index: number): number =>
    colWidths[index] ?? columns[index]?.width ?? DEFAULT_COLUMN_WIDTH;

  const totalWidth = columns.reduce((sum, _, index) => sum + widthAt(index), 0);

  return (
    <div
      className={cn(
        "max-h-[min(65svh,44rem)] overflow-auto transition-opacity",
        loading && "opacity-55",
      )}
    >
      <table
        className="table-fixed border-separate border-spacing-0 text-sm"
        style={{ width: `${String(totalWidth)}px`, minWidth: "100%" }}
      >
        {/*
            ONE <col> PER COLUMN, sized from the SAME live `colWidths` every cell below
            reads — `table-fixed` makes these authoritative rather than a hint the browser
            can override from content, which is what makes a drag actually stick instead of
            the column snapping back to fit its widest cell.
          */}
        <colgroup>
          {columns.map((column, index) => (
            <col
              key={column.key}
              style={{ width: `${String(widthAt(index))}px` }}
            />
          ))}
        </colgroup>
        <thead>
          {banded && (
            <tr>
              {bands.map((band, index) => {
                // Where this band starts, so an ungrouped band can find ITS column and
                // render that column's own label.
                const start = bands
                  .slice(0, index)
                  .reduce((sum, b) => sum + b.span, 0);
                const own = band.label === null ? columns[start] : undefined;
                // A band is FROZEN when the column it STARTS at is — which happens for
                // more than the leading ungrouped column now that "Platform" freezes too
                // (see ReportColumn.sticky). Its width is the summed width of every sticky
                // column it spans, so the band header lines up with the body cells under it
                // exactly the way an unbanded sticky column already does.
                const startOffset = offsets[start];
                let bandWidth = 0;
                for (let i = start; i < start + band.span; i += 1)
                  bandWidth += widthAt(i);
                return (
                  <th
                    key={`${band.label ?? "plain"}:${String(index)}`}
                    colSpan={band.span}
                    // The ungrouped leading column spans both header rows, so "Source" sits
                    // beside the bands rather than above an empty cell.
                    rowSpan={band.label === null ? 2 : undefined}
                    className={cn(
                      "row-head text-foreground relative sticky top-0 z-20 h-11 border-b border-r px-3 text-center text-[13px] font-semibold",
                      band.label === null && "text-left align-middle",
                      index > 0 && "border-l",
                      // A frozen band. Without it, scrolling right to reach the last-click
                      // columns leaves every row and the footer unlabelled — which is how a
                      // page total gets read as the range's total.
                      startOffset !== undefined && "z-30",
                    )}
                    style={
                      startOffset === undefined
                        ? undefined
                        : {
                            left: `${String(startOffset)}px`,
                            width: `${String(bandWidth)}px`,
                          }
                    }
                  >
                    {own === undefined ? (
                      <BandLabel label={band.label} />
                    ) : (
                      <>
                        {/*
                         * THE UNGROUPED COLUMN'S OWN LABEL. It spans both header rows, and the
                         * leaf row skips it — so without this the first column had NO header
                         * at all. Every banded report shipped with a blank cell above Source
                         * and Campaign, which is the one column a merchant reads to know what
                         * a row IS. Found 2026-09-03 by a test written for the skeleton.
                         */}
                        <HeaderLabel
                          column={own}
                          sortBy={sortBy}
                          direction={direction}
                          onSort={onSort}
                          numeric={false}
                        />
                        <ResizeHandle index={start} onResize={onResize} />
                      </>
                    )}
                  </th>
                );
              })}
            </tr>
          )}
          <tr>
            {columns.map((column, index) => {
              if (banded && column.group === undefined) return null;
              const isSorted =
                column.sortKey !== undefined && column.sortKey === sortBy;
              const numeric = column.kind !== "text" && column.kind !== "week";
              return (
                <th
                  key={column.key}
                  aria-sort={
                    isSorted
                      ? direction === "desc"
                        ? "descending"
                        : "ascending"
                      : undefined
                  }
                  className={cn(
                    "text-muted-foreground row-head relative sticky z-20 border-b border-r pl-3 pr-4 py-2.5 text-[13px] font-medium whitespace-nowrap",
                    // Pinned BELOW the band row when there is one — `top-11` matches the
                    // `h-11` above it. A banded table has two header rows and they must
                    // stack, not overlap.
                    banded ? "top-11" : "top-0",
                    numeric ? "text-right" : "text-left",
                    bandStarts.has(index) && index > 0 && "border-l",
                    offsets[index] !== undefined && "z-30",
                  )}
                  style={columnStyle(widthAt(index), offsets[index])}
                >
                  <HeaderLabel
                    column={column}
                    sortBy={sortBy}
                    direction={direction}
                    onSort={onSort}
                    numeric={numeric}
                  />
                  <ResizeHandle
                    index={index}
                    onResize={onResize}
                    spansBandRow={banded}
                  />
                </th>
              );
            })}
          </tr>
        </thead>

        {children}
      </table>
    </div>
  );
}

/**
 * A column's header: its label, and a sort control when the API can sort by it.
 *
 * Shared by the leaf header row and by the ungrouped column that spans both rows, so the
 * first column is labelled the same way as every other — it was blank before, because the
 * band row rendered a group name it did not have and the leaf row skipped it entirely.
 */
function HeaderLabel({
  column,
  sortBy,
  direction,
  onSort,
  numeric,
}: {
  readonly column: ReportColumn;
  readonly sortBy: string | null;
  readonly direction: "asc" | "desc";
  readonly onSort: (sortKey: string) => void;
  readonly numeric: boolean;
}): React.JSX.Element {
  if (column.sortKey === undefined) return <>{column.label}</>;

  const isSorted = column.sortKey === sortBy;
  return (
    <button
      type="button"
      onClick={() => onSort(column.sortKey ?? "")}
      className={cn(
        "hover:text-foreground inline-flex items-center gap-1 font-medium transition-colors",
        numeric && "flex-row-reverse",
      )}
    >
      {column.label}
      {/* The inactive icon is a NEUTRAL double chevron, as legacy shows — it says
          "sortable" without claiming a direction. */}
      {isSorted ? (
        direction === "desc" ? (
          <ChevronDown className="size-3.5" aria-hidden="true" />
        ) : (
          <ChevronUp className="size-3.5" aria-hidden="true" />
        )
      ) : (
        <ChevronsUpDown className="size-3 opacity-40" aria-hidden="true" />
      )}
    </button>
  );
}
